import React, { useEffect, useState, useContext } from "react";
import { Spinner, Alert, Container, Card, Button, Row, Col, Form, Collapse } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import { Input } from 'reactstrap';
import HusariaHeader from "views/pages/components/HusariaHeader";
import paginationFactory from "react-bootstrap-table2-paginator";
import BootstrapTable from "react-bootstrap-table-next";
import { hasPermission, PERMISSIONS } from "utils/permissions";

const CDR = () => {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const visibleColumns = [
        { key: "caller_destination", label: "Extension" },
        { key: "caller_id_name", label: "Caller Name" },
        { key: "caller_id_number", label: "Caller Number" },
        { key: "caller_destination", label: "Caller Destination" },
        { key: "destination_number", label: "Destination" },
        { key: "start_stamp", label: "Start" },
        { key: "end_stamp", label: "End" },
        { key: "codecs", label: "Codecs" },
        { key: "tta", label: "TTA" },
        { key: "rtp_audio_in_mos", label: "MOS" },
        { key: "", label: "PDD" },
        { key: "duration", label: "Duration" },
        { key: "status", label: "Status" },
    ];
    const tableName = "v_xml_cdr";
    const title = "Call Detail Records";
    const parentName = "HUCS";
    const rowKey = "xml_cdr_uuid";
    const userData = React.useMemo(() => {
        const storedUser = localStorage.getItem("user");
        return storedUser ? JSON.parse(storedUser) : null;
      }, []);
    const canEdit = hasPermission(userData, PERMISSIONS.EDIT);
    const [rows, setRows] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const navigate = useNavigate();

    // Filter states
    const [filters, setFilters] = useState({
        direction: "",
        leg: "",
        startDateFrom: "",
        startTimeFrom: "",
        startDateTo: "",
        startTimeTo: "",
        readCodec: "",
        writeCodec: "",
        status: "",
        durationMin: "",
        durationMax: "",
        ttaMin: "",
        ttaMax: "",
        ringGroup: "",
        extension: "",
        callerDestination: "",
        hangupCause: "",
        ivrMenu: "",
        callerIdName: "",
        callerIdNumber: "",
        destination: "",
        hasRecording: ""
    });

    // Dropdown options
    const directionOptions = ["Inbound", "Outbound", "Local"];
    const legOptions = ["a", "b"];
    const statusOptions = ["Answered", "No Answer", "Busy", "Missed", "Voicemail", "Cancelled", "Failed"];
    const recordingOptions = [
        { value: "", label: "All" },
        { value: "true", label: "Yes" },
        { value: "false", label: "No" }
    ];
    const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
    const site_uuid = currentSite?.site_uuid || null;
    
    const fetchRows = async () => {
        setLoading(true);
        try {
            
            const response = await fetch(
                `${serverHost}/get_rows?table=${tableName}&pageSize=1000&page=1&domain_uuid=${site_uuid}`
            );
            if (!response.ok) throw new Error(`Failed to fetch ${tableName}`);
            const data = await response.json();
            let values = data.results;

            values.map((r) => {
                r.tta = r.waitsec !== '0' ? r.waitsec : null;
                r.codecs = r.read_codec + " / " + r.write_codec;
            });
            
            setRows(values || []);
            setFilteredRows(values || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const columns = visibleColumns.map((col, index) => ({
        dataField: col.key,
        text: col.label,
        sort: true,
        formatter: (cell, row) => {
          if (index === 0) {
            return canEdit ? (
              <a
                style={{
                  color: isDark ? "#60a5fa" : "#0d6efd",
                  fontWeight: 600,
                  cursor: "pointer",
                  textDecoration: "none",
                }}
              >
                {cell ?? "-"}
              </a>
            ) : (
              <span style={{ fontWeight: 600 }}>{cell ?? "-"}</span>
            );
          }
    
          return cell ?? "-";
        },
      }));
    
      const rowEvents = {
        onClick: (e, row, rowIndex) => {
          console.log(row[rowKey]);
          navigate(`/admin/hucs/view/call-detail-record/${row[rowKey]}`);
        },
      };

    useEffect(() => {
        fetchRows();
    }, []);

    const applyFilters = () => {
        let filtered = [...rows];

        // Direction filter
        if (filters.direction) {
            filtered = filtered.filter(row => 
                row.direction?.toLowerCase() === filters.direction.toLowerCase()
            );
        }

        // Leg filter
        if (filters.leg) {
            filtered = filtered.filter(row => 
                row.leg?.toLowerCase() === filters.leg.toLowerCase()
            );
        }

        // Status filter
        if (filters.status) {
            filtered = filtered.filter(row => 
                row.status?.toLowerCase() === filters.status.toLowerCase()
            );
        }

        // Recording filter
        if (filters.hasRecording) {
            const hasRecording = filters.hasRecording === "true";
            filtered = filtered.filter(row => 
                hasRecording ? row.record_path : !row.record_path
            );
        }

        // Date/Time range filters
        if (filters.startDateFrom || filters.startTimeFrom) {
            const fromDateTime = new Date(`${filters.startDateFrom || '1970-01-01'} ${filters.startTimeFrom || '00:00'}`);
            filtered = filtered.filter(row => {
                const rowDate = new Date(row.start_stamp);
                return rowDate >= fromDateTime;
            });
        }

        if (filters.startDateTo || filters.startTimeTo) {
            const toDateTime = new Date(`${filters.startDateTo || '2099-12-31'} ${filters.startTimeTo || '23:59'}`);
            filtered = filtered.filter(row => {
                const rowDate = new Date(row.start_stamp);
                return rowDate <= toDateTime;
            });
        }

        // Codec filters
        if (filters.readCodec) {
            filtered = filtered.filter(row => 
                row.read_codec?.toLowerCase().includes(filters.readCodec.toLowerCase())
            );
        }

        if (filters.writeCodec) {
            filtered = filtered.filter(row => 
                row.write_codec?.toLowerCase().includes(filters.writeCodec.toLowerCase())
            );
        }

        // Duration filters
        if (filters.durationMin) {
            filtered = filtered.filter(row => 
                parseFloat(row.duration) >= parseFloat(filters.durationMin)
            );
        }

        if (filters.durationMax) {
            filtered = filtered.filter(row => 
                parseFloat(row.duration) <= parseFloat(filters.durationMax)
            );
        }

        // TTA filters
        if (filters.ttaMin) {
            filtered = filtered.filter(row => 
                parseFloat(row.tta || 0) >= parseFloat(filters.ttaMin)
            );
        }

        if (filters.ttaMax) {
            filtered = filtered.filter(row => 
                parseFloat(row.tta || 0) <= parseFloat(filters.ttaMax)
            );
        }

        // Text field filters
        if (filters.ringGroup) {
            filtered = filtered.filter(row => 
                row.ring_group?.toLowerCase().includes(filters.ringGroup.toLowerCase())
            );
        }

        if (filters.extension) {
            filtered = filtered.filter(row => 
                row.extension?.toLowerCase().includes(filters.extension.toLowerCase())
            );
        }

        if (filters.callerDestination) {
            filtered = filtered.filter(row => 
                row.caller_destination?.toLowerCase().includes(filters.callerDestination.toLowerCase())
            );
        }

        if (filters.hangupCause) {
            filtered = filtered.filter(row => 
                row.hangup_cause?.toLowerCase().includes(filters.hangupCause.toLowerCase())
            );
        }

        if (filters.ivrMenu) {
            filtered = filtered.filter(row => 
                row.ivr_menu?.toLowerCase().includes(filters.ivrMenu.toLowerCase())
            );
        }

        if (filters.callerIdName) {
            filtered = filtered.filter(row => 
                row.caller_id_name?.toLowerCase().includes(filters.callerIdName.toLowerCase())
            );
        }

        if (filters.callerIdNumber) {
            filtered = filtered.filter(row => 
                row.caller_id_number?.toLowerCase().includes(filters.callerIdNumber.toLowerCase())
            );
        }

        if (filters.destination) {
            filtered = filtered.filter(row => 
                row.destination_number?.toLowerCase().includes(filters.destination.toLowerCase())
            );
        }

        setFilteredRows(filtered);
    };

    const handleApplyFilters = () => {
        applyFilters();
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const exportCDR = async () => {
        try {
            const response = await fetch(`${serverHost}/export_csv/${site_uuid}`);
            if (!response.ok) throw new Error("Download failed");
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${currentSite.company_name}_cdr_data.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            alert(`Error downloading recording: ${err.message}`);
        }
    }

    const handleResetFilters = () => {
        setFilters({
            direction: "",
            leg: "",
            startDateFrom: "",
            startTimeFrom: "",
            startDateTo: "",
            startTimeTo: "",
            readCodec: "",
            writeCodec: "",
            status: "",
            durationMin: "",
            durationMax: "",
            ttaMin: "",
            ttaMax: "",
            ringGroup: "",
            extension: "",
            callerDestination: "",
            hangupCause: "",
            ivrMenu: "",
            callerIdName: "",
            callerIdNumber: "",
            destination: "",
            hasRecording: ""
        });
        setFilteredRows(rows);
    };

    const singularTitle = title.endsWith("s") ? title.slice(0, -1) : title;

    const pagination = paginationFactory({
    page: 1,
    alwaysShowAllBtns: true,
    showTotal: true,
    withFirstAndLast: false,
    sizePerPageRenderer: ({ onSizePerPageChange }) => (
      <div className="dataTables_length">
        <label>
          Show{" "}
          <select
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries.
        </label>
      </div>
    ),
  });

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc", // "asc" | "desc"
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

    if (loading && rows.length === 0)
        return (
            <div className="text-center mt-5">
                <Spinner animation="border" variant={isDark ? "light" : "primary"} />
                <p className="mt-2" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                    Loading {title?.toLowerCase() || "data"}...
                </p>
            </div>
        );

    if (error)
        return (
            <Alert variant="danger" className="mt-5 text-center">
                {error}
            </Alert>
        );

    return (
        <>
            <HusariaHeader name={title} parentName={parentName} padding="4.5rem"/>
            <Container fluid className="mt--6">
                {/* Filters Card */}
                <Card className={`shadow-sm mb-3 ${isDark ? "bg-dark text-light" : ""}`}>
                    <Card.Header
                        style={{
                            backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                            color: isDark ? "#e2e8f0" : "#111",
                        }}
                        className="d-flex justify-content-between align-items-center"
                    >
                        <h5 className="mb-0">Search Filters</h5>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            {showFilters ? "Hide Filters" : "Show Filters"}
                        </Button>
                    </Card.Header>
                    
                    <Collapse in={showFilters}>
                        <Card.Body className="page-menu" style={{ backgroundColor: isDark ? "#1f2937" : "#fff"}}>
                            <Form>
                                <Row className="mb-3">
                                    {/* Direction */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Direction
                                            </Form.Label>
                                            <Input
                                                type="select"
                                                value={filters.direction}
                                                onChange={(e) => handleFilterChange("direction", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            >
                                                <option value="">All Directions</option>
                                                {directionOptions.map(opt => (
                                                    <option key={opt} value={opt}>{opt}</option>
                                                ))}
                                            </Input>
                                        </Form.Group>
                                    </Col>

                                    {/* Leg */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Leg
                                            </Form.Label>
                                            <Input
                                                type="select"
                                                value={filters.leg}
                                                onChange={(e) => handleFilterChange("leg", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            >
                                                <option value="">All Legs</option>
                                                {legOptions.map(opt => (
                                                    <option key={opt} value={opt}>{opt}</option>
                                                ))}
                                            </Input>
                                        </Form.Group>
                                    </Col>

                                    {/* Status */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Status
                                            </Form.Label>
                                            <Input
                                                type="select"
                                                value={filters.status}
                                                onChange={(e) => handleFilterChange("status", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            >
                                                <option value="">All Statuses</option>
                                                {statusOptions.map(opt => (
                                                    <option key={opt} value={opt}>{opt}</option>
                                                ))}
                                            </Input>
                                        </Form.Group>
                                    </Col>

                                    {/* Has Recording */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Recording
                                            </Form.Label>
                                            <Input
                                                type="select"
                                                value={filters.hasRecording}
                                                onChange={(e) => handleFilterChange("hasRecording", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            >
                                                {recordingOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </Input>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    {/* Start Date From */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Start Date From
                                            </Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={filters.startDateFrom}
                                                onChange={(e) => handleFilterChange("startDateFrom", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Start Time From */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Start Time From
                                            </Form.Label>
                                            <Form.Control
                                                type="time"
                                                value={filters.startTimeFrom}
                                                onChange={(e) => handleFilterChange("startTimeFrom", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Start Date To */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Start Date To
                                            </Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={filters.startDateTo}
                                                onChange={(e) => handleFilterChange("startDateTo", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Start Time To */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Start Time To
                                            </Form.Label>
                                            <Form.Control
                                                type="time"
                                                value={filters.startTimeTo}
                                                onChange={(e) => handleFilterChange("startTimeTo", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    {/* Read Codec */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Read Codec
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="e.g., PCMU"
                                                value={filters.readCodec}
                                                onChange={(e) => handleFilterChange("readCodec", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Write Codec */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Write Codec
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="e.g., PCMU"
                                                value={filters.writeCodec}
                                                onChange={(e) => handleFilterChange("writeCodec", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Duration Min */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Min Duration (sec)
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                placeholder="0"
                                                value={filters.durationMin}
                                                onChange={(e) => handleFilterChange("durationMin", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Duration Max */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Max Duration (sec)
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                placeholder="999999"
                                                value={filters.durationMax}
                                                onChange={(e) => handleFilterChange("durationMax", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    {/* TTA Min */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Min TTA (sec)
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                placeholder="0"
                                                value={filters.ttaMin}
                                                onChange={(e) => handleFilterChange("ttaMin", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* TTA Max */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Max TTA (sec)
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                placeholder="999999"
                                                value={filters.ttaMax}
                                                onChange={(e) => handleFilterChange("ttaMax", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Ring Group */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Ring Group
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Ring Group"
                                                value={filters.ringGroup}
                                                onChange={(e) => handleFilterChange("ringGroup", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Extension */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Extension
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Extension"
                                                value={filters.extension}
                                                onChange={(e) => handleFilterChange("extension", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    {/* Caller Destination */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Caller Destination
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Caller Destination"
                                                value={filters.callerDestination}
                                                onChange={(e) => handleFilterChange("callerDestination", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Hangup Cause */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Hangup Cause
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Hangup Cause"
                                                value={filters.hangupCause}
                                                onChange={(e) => handleFilterChange("hangupCause", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* IVR Menu */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                IVR Menu
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="IVR Menu"
                                                value={filters.ivrMenu}
                                                onChange={(e) => handleFilterChange("ivrMenu", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Caller ID Name */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Caller ID Name
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Caller ID Name"
                                                value={filters.callerIdName}
                                                onChange={(e) => handleFilterChange("callerIdName", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    {/* Caller ID Number */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Caller ID Number
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Caller ID Number"
                                                value={filters.callerIdNumber}
                                                onChange={(e) => handleFilterChange("callerIdNumber", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Destination */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                Destination
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Destination Number"
                                                value={filters.destination}
                                                onChange={(e) => handleFilterChange("destination", e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? "#374151" : "#fff",
                                                    color: isDark ? "#e2e8f0" : "#212529",
                                                    borderColor: isDark ? "#4b5563" : "#ced4da"
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Filter Action Buttons */}
                                <Row>
                                    <Col className="d-flex justify-content-end gap-2">
                                        <Button 
                                            variant="light" 
                                            onClick={handleResetFilters}
                                        >
                                            Reset Filters
                                        </Button>
                                        <Button 
                                            variant="primary" 
                                            onClick={handleApplyFilters}
                                        >
                                            Apply Filters
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Collapse>
                </Card>

                {/* Table Card */}
                <Card className={`shadow-sm ${isDark ? "bg-dark text-light" : ""}`}>
                    <Card.Header
                        style={{
                            backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                            color: isDark ? "#e2e8f0" : "#111",
                        }}
                        className="d-flex justify-content-between align-items-center"
                    >
                        <div className="d-flex gap-2">
                            <Button variant="info" size="sm" onClick={fetchRows}>
                                Refresh
                            </Button>
                            <Button variant="success" size="sm" onClick={exportCDR}>
                                Export
                            </Button>
                        </div>
                    </Card.Header>

                    <Card.Body style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}>
                        {loading && (
                            <div className="text-center p-3">
                                <Spinner animation="border" size="sm" variant={isDark ? "light" : "primary"} />
                            </div>
                        )}
                        <div>
              <div className="py-4 table-responsive">
                <BootstrapTable
                  keyField={rowKey}
                  data={filteredRows} // <-- use filteredRows instead of all rows
                  columns={columns}
                  bootstrap4
                  pagination={pagination}
                  bordered={false}
                  hover
                  rowEvents={canEdit ? rowEvents : {}}
                  rowStyle={{ cursor: canEdit ? "pointer" : "default" }}
                  classes="align-items-center table-flush"
                  noDataIndication={() =>
                    loading ? (
                      <div className="text-center">Loading...</div>
                    ) : (
                      <div className="text-center">No records found.</div>
                    )
                  }
                />
              </div>
            </div>
                    </Card.Body>
                </Card>
            </Container>
        </>
    );
};

export default CDR;